import { Image } from 'expo-image';
import { Link } from 'expo-router';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { tokens } from '../../tokens';
import { Button } from '../button';
import { Pressable } from '../pressable';
import { XStack, YStack } from '../stack';
import { Text } from '../text';

import { QuantityBadge } from './quantity-badge';
import { OrderDisplayItem, OrderDisplayProps, OrderDisplayType } from './types';

const ITEM_GAP = 4;
const NUM_ITEMS_TO_DISPLAY = 4;

function OrderCardItemList({ items }: { items: OrderDisplayItem[] }) {
  const [itemsWrapperWidth, setItemsWrapperWidth] = useState<number | null>(null);

  return (
    <XStack
      style={styles.itemsWrapper}
      onLayout={event => setItemsWrapperWidth(event.nativeEvent.layout.width)}
    >
      {items.slice(0, NUM_ITEMS_TO_DISPLAY).map((item, index, sliceArr) => {
        // For the last element, we want to display the amount of remaining items.
        // This is checking if the item in the loop is the last in the slice or in the limit we defined.
        const isLastItem = index === items.length - 1;
        const isLastItemInSlice = index === sliceArr.length - 1;
        const isOverflowItem = isLastItemInSlice && !isLastItem;

        return (
          <XStack
            style={[
              styles.item,
              itemsWrapperWidth
                ? {
                    width:
                      // We have to account for the space between items when trying to evenly divide the available space
                      (itemsWrapperWidth - Math.max(sliceArr.length, 0) * ITEM_GAP) /
                      sliceArr.length,
                  }
                : {},
            ]}
            key={index}
          >
            {isOverflowItem ? (
              <Text.Paragraph size="sm">+{items.length - index}</Text.Paragraph>
            ) : (
              <>
                {item.image && (
                  <Image
                    style={styles.itemImage}
                    source={item.image}
                    alt={item.name}
                    contentFit="contain"
                    testID="display-item-image"
                  />
                )}
                {(items.length === 1 || !item.image) && (
                  <Text.Paragraph weight="semibold" size="md" numberOfLines={1}>
                    {item.name}
                  </Text.Paragraph>
                )}
                {item.quantity > 1 && (
                  <QuantityBadge text={`${item.quantity}`} style={styles.quantityBadge} />
                )}
              </>
            )}
          </XStack>
        );
      })}
    </XStack>
  );
}

export function OrderDisplayMobile({
  type = OrderDisplayType.ORDER_IN_PROGRESS,
  ...props
}: OrderDisplayProps) {
  if (type === OrderDisplayType.ORDER_IN_PROGRESS) {
    const { storeName, items, href, style, ...viewProps } = props;

    return (
      <Link href={href} asChild>
        <Pressable borderRadius={9} {...viewProps}>
          <YStack style={[styles.container, style]}>
            <XStack style={styles.gap16}>
              <YStack style={styles.storeNameTextContentWrapper}>
                <Text.Paragraph weight="semibold" size="md" numberOfLines={1}>
                  Order In Progress
                </Text.Paragraph>
                <Text.Paragraph size="sm" numberOfLines={1}>
                  {storeName}
                </Text.Paragraph>
              </YStack>
              <View style={styles.buttonWrapper}>
                <Button type="solid" size="sm">
                  <Button.Text>Track Order</Button.Text>
                </Button>
              </View>
            </XStack>
            <OrderCardItemList items={items} />
          </YStack>
        </Pressable>
      </Link>
    );
  } else if (type === OrderDisplayType.REORDER) {
    const {
      storeName,
      items,
      href,
      style,
      serviceModeTitle,
      reorderCallback,
      reorderLoading,
      ...viewProps
    } = props;

    return (
      <YStack {...viewProps}>
        <YStack style={[styles.container, style]}>
          <XStack style={styles.gap16}>
            <YStack style={styles.storeNameTextContentWrapper}>
              <Text.Paragraph weight="semibold" size="md" numberOfLines={1}>
                {storeName}
              </Text.Paragraph>
              <Text.Paragraph size="sm" numberOfLines={1}>
                {serviceModeTitle}
              </Text.Paragraph>
            </YStack>
            <View style={styles.buttonWrapper}>
              <Button type="solid" size="sm" onPress={reorderCallback} loading={reorderLoading}>
                <Button.Text>Reorder</Button.Text>
              </Button>
            </View>
          </XStack>
          <Link href={href} asChild>
            <Pressable testID="reorder-card-redirect">
              <OrderCardItemList items={items} />
            </Pressable>
          </Link>
        </YStack>
      </YStack>
    );
  }

  if (__DEV__) {
    throw new Error(`OrderDisplay has no implementation for type '${type}'`);
  }

  return null;
}

const styles = StyleSheet.create({
  gap16: {
    gap: 16,
  },
  container: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    backgroundColor: tokens.colors.$white,
    gap: 12,
    padding: 16,
  },
  storeNameTextContentWrapper: {
    flexGrow: 1,
    flexShrink: 1,
    justifyContent: 'center',
  },
  buttonWrapper: {
    flexShrink: 0,
    flexGrow: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemsWrapper: {
    gap: ITEM_GAP,
  },
  item: {
    position: 'relative',
    paddingVertical: 4,
    paddingHorizontal: 8,
    flexGrow: 1,
    flexShrink: 1,
    backgroundColor: tokens.colors.$blackOpacity04,
    borderRadius: 4,
    gap: 8,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  quantityBadge: {
    right: 9,
    bottom: -5,
  },
  itemImage: {
    flexShrink: 1,
    flexBasis: 44,
    aspectRatio: 1 / 1,
  },
});
